import { ArrowRight, Check } from 'lucide-react';
import { Link } from 'react-router-dom';

import { useAuth } from '@/features/auth/hooks/useAuth';
import { ECOSSISTEMA, MARCA } from '@/features/landing/conteudo';
import { ROTAS } from '@/shared/lib/rotas';
import { Button } from '@/shared/ui/button';

/**
 * Hero em gradiente de marca, com os mesmos hex e os mesmos três glows das
 * telas de auth (`AuthShell`). Os literais são de propósito, pelo mesmo motivo
 * de lá: este bloco é escuro nos DOIS temas, então não pode depender das CSS
 * variables — que invertem no tema light e apagariam o texto branco.
 *
 * A headline vem do posicionamento da marca (`identidadevisual.md`):
 * "transforma exames em direção", sem nunca substituir a decisão do
 * profissional. É esta frase que o resto da página sustenta.
 */
export function SecaoHero() {
  const { autenticado, carregando } = useAuth();
  const logado = autenticado && !carregando;

  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#691241] via-[#975ea1] to-[#15acb7]">
      <div className="absolute left-10 top-10 h-72 w-72 rounded-full bg-[#a2195b]/30 blur-3xl" />
      <div className="absolute -bottom-16 right-10 h-96 w-96 rounded-full bg-[#15acb7]/20 blur-3xl" />
      <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#975ea1]/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 py-20 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:py-28">
        <div>
          <span className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/90 backdrop-blur-sm">
            {MARCA.grupo}
          </span>

          <h1 className="mt-6 text-4xl font-extrabold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Toda a jornada do <span className="text-nohra-turquoise-light">paciente endócrino, conectada</span>.
          </h1>

          <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/85">
            A NOHRA Clinic reúne exames, histórico, prescrições e evolução em um só lugar, para que você encontre rapidamente o que precisa e acompanhe cada caso com mais clareza. Mais contexto para você. Mais continuidade para o paciente.
          </p>

          <div className="mt-9 flex flex-col gap-3 sm:flex-row">
            {logado ? (
              <Button
                asChild
                size="lg"
                className="bg-white text-[#691241] shadow-lg hover:bg-white/90"
              >
                <Link to={ROTAS.dashboard}>
                  Abrir plataforma
                  <ArrowRight className="ml-1" />
                </Link>
              </Button>
            ) : (
              <>
                <Button
                  asChild
                  size="lg"
                  className="bg-white text-[#691241] shadow-lg hover:bg-white/90"
                >
                  <Link to={ROTAS.registro}>
                    Começar agora
                    <ArrowRight className="ml-1" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
                >
                  <Link to={ROTAS.login}>Já tenho uma conta</Link>
                </Button>
              </>
            )}
          </div>

          <p className="mt-5 flex items-center gap-2 text-sm text-white/70">
            <Check className="h-4 w-4 shrink-0" />
            Cadastro com CRMV. Acesso pelo navegador e pelo aplicativo.
          </p>
        </div>

        <MockResultado />
      </div>

      <div className="relative border-t border-white/15">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-8 gap-y-3 px-4 py-5 sm:px-6">
          <span className="text-xs font-medium uppercase tracking-wider text-white/50">
            Nossas marcas
          </span>
          {ECOSSISTEMA.map((marca) => (
            <span key={marca} className="text-sm font-semibold text-white/75">
              {marca}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Prévia da tela de resultados — o diferencial do produto em uma imagem:
 * resultado, faixa de referência e interpretação lado a lado.
 *
 * Marcado como **exemplo ilustrativo** na própria peça, e sem número de faixa:
 * é uma landing lida por médicos veterinários, e valor de referência inventado
 * numa página institucional é o tipo de detalhe que alguém leva a sério.
 */
function MockResultado() {
  const analitos = [
    { nome: 'Cortisol Basal (CLIA)', situacao: 'Dentro da faixa de referência', dentro: true },
    { nome: 'T4T (RIE)', situacao: 'Acima da faixa de referência', dentro: false },
    { nome: 'Gastrina (RIE)', situacao: 'Dentro da faixa de referência', dentro: true },
  ];

  return (
    <div className="relative mx-auto w-full max-w-md lg:mx-0">
      <div className="rounded-2xl border border-white/20 bg-white/10 p-2 shadow-2xl backdrop-blur-md">
        <div className="rounded-xl bg-[#12122a]/90 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-white/50">Resultado de exame</p>
              <p className="mt-1 text-base font-semibold text-white">Painel hormonal</p>
            </div>
            <span className="rounded-full bg-[#15acb7]/20 px-2.5 py-1 text-xs font-semibold text-[#8dcfdb]">
              Interpretado
            </span>
          </div>

          <div className="mt-5 space-y-2.5">
            {analitos.map((analito) => (
              <div
                key={analito.nome}
                className="flex items-center justify-between rounded-lg border border-white/10 bg-white/5 px-3 py-2.5"
              >
                <span className="text-sm text-white/80">{analito.nome}</span>
                <span
                  className={
                    analito.dentro
                      ? 'text-xs font-semibold text-[#8dcfdb]'
                      : 'text-xs font-semibold text-[#f0a5c8]'
                  }
                >
                  {analito.situacao}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-5 rounded-lg border border-white/10 bg-white/5 px-3 py-3">
            <p className="text-xs text-white/50">Comparado com o exame anterior</p>
            <p className="mt-1 text-sm text-white/80">
              Histórico do paciente na mesma tela, sem abrir outro documento.
            </p>
          </div>
        </div>
      </div>

      <p className="mt-3 text-center text-xs text-white/50 lg:text-left">
        Exemplo ilustrativo da interface.
      </p>
    </div>
  );
}
